import { getApiBase } from '../utils/apiBase';
import { getAttributionSnapshot } from '../utils/attribution';
/**
 * Connections + Lead Ingestion service.
 *
 * Sequential dual-write (Jun 2026 P0 fix):
 *   1) POST /api/connections/    — creates the connection audit doc,
 *                                  dedupes the contact, classifies
 *                                  contact / job_application / newsletter.
 *                                  For booking / group_booking the
 *                                  classifier is a no-op; the opportunity
 *                                  is created by step 2.
 *   2) POST /api/integrations/leads  — creates the actual Opportunity
 *                                  (with the connection_id from step 1
 *                                  attached, so admin source-attribution
 *                                  works) and fires the legacy CRM
 *                                  webhook exactly once.
 *
 *   Sequential ordering is deliberate: lead-ingestion needs the
 *   connection_id from step 1 to populate `opportunity.connection_id`.
 *   The serial overhead is one round-trip (~50-100 ms); both calls
 *   already swallow errors silently so the visible form UX is unchanged.
 *
 * Feb 2026 — Attribution snapshot:
 *   Every payload sent through this helper is auto-decorated with a
 *   `marketing_attribution` object (first_touch_*, last_touch_*,
 *   utm_*, click-IDs, landing_page_url, page_url, referrer_url) read
 *   from localStorage by getAttributionSnapshot(). The backend
 *   lead-ingestion path picks this up via `marketing_attribution` row
 *   key (when present in the field_map) AND/OR we forward it as a
 *   top-level static-style override so all 7+ public form_ids attach
 *   attribution without per-form code changes. Purely additive — when
 *   the user has no UTMs / referrers, the snapshot keys are present
 *   but empty strings.
 */
const API_BASE_URL = getApiBase() || '';

async function parseJsonSafely(response) {
  try {
    return await response.json();
  } catch (_) {
    return null;
  }
}

function responseError(body, fallback) {
  return new Error((body && (body.detail || body.message || body.error)) || fallback);
}

/**
 * @param {"booking"|"group_booking"|"newsletter"|"job_application"|"contact"} type
 * @param {object} payload  - full form data, stored as-is.
 *                            MUST include `form_id` for CRM ingestion.
 * @param {object} source   - { page, component, reference_id }
 */
export async function captureConnection(type, payload, source = {}) {
  // Read attribution snapshot from localStorage (first-touch + last-touch
  // captured by initAttributionCapture on app mount). Safe defaults if
  // anything fails — never blocks form submission.
  let attribution = {};
  try { attribution = getAttributionSnapshot() || {}; } catch (_) { attribution = {}; }

  // Decorate payload with attribution. Existing payload keys win — we
  // never overwrite anything the form set explicitly. We also attach a
  // structured `marketing_attribution` object so the backend ingestion
  // path can persist it directly onto opportunity.marketing_attribution
  // (via row-level handling in /api/integrations/leads — see Feb 2026
  // attribution gap-fill).
  const enrichedPayload = {
    ...attribution,
    ...payload,
    marketing_attribution: {
      ...attribution,
      ...(payload && payload.marketing_attribution ? payload.marketing_attribution : {}),
    },
  };

  // ----- Step 1: connections audit + classification -----
  let connectionId = null;
  let connection = null;
  let connectionError = null;
  try {
    const res = await fetch(`${API_BASE_URL}/api/connections/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, payload: enrichedPayload, source }),
    });
    const body = await parseJsonSafely(res);
    if (!res.ok) {
      throw responseError(body, 'Connection audit failed');
    }
    connection = body;
    connectionId = (body && body.id) || null;
  } catch (error) {
    connectionError = error;
  }

  // ----- Step 2: CRM lead ingestion (Opportunity + legacy CRM webhook) -----
  // Only fires when a form_id is present. The connection_id from step 1
  // is forwarded as a top-level field so the backend can attach it to
  // the opportunity doc.
  if (enrichedPayload && enrichedPayload.form_id) {
    const res = await fetch(`${API_BASE_URL}/api/integrations/leads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        form_id: enrichedPayload.form_id,
        connection_id: connectionId,
        row_data: [{
          ...enrichedPayload,
          source_page: (source && source.page) || undefined,
          source_component: (source && source.component) || undefined,
        }],
      }),
    });
    const body = await parseJsonSafely(res);
    if (!res.ok) {
      const error = responseError(body, 'Lead submission failed');
      error.connectionId = connectionId;
      error.connectionError = connectionError ? connectionError.message : null;
      throw error;
    }

    return {
      connectionId,
      connection,
      connectionError: connectionError ? connectionError.message : null,
      lead: body,
    };
  }

  if (connectionError) {
    throw connectionError;
  }

  return {
    connectionId,
    connection,
    connectionError: null,
    lead: null,
  };
}
